import { invoke } from '@tauri-apps/api/core';
import { useExtensionStore } from '../store';
import { isShippedBundledExtension } from '../bundledIds';
import { KNOWN_CAPABILITIES } from '../registry';

/**
 * Capability enforcement for HOST-RENDERED extension surfaces.
 *
 * `hostBridge.js` checks the grant set before dispatching anything a guest
 * iframe asks for, and for a long time that was described as "the" check.
 * It is only the check on the guest RPC path. DB Manager's results grid,
 * its schema introspection helpers, and the host-side connect helper are
 * first-party React that calls the same `db_*` / `secure_storage_*` Tauri
 * commands directly, passing the tab's `extensionId` — so a user who turned
 * `db:mysql` or `secureStorage` OFF in Settings still had a fully working
 * query grid reading their stored password and running arbitrary SQL. The
 * toggle governed the sidebar tree and nothing else. (EXT-05a.)
 *
 * `invokeAsExtension` is the single funnel those call sites now use. Routing
 * by COMMAND NAME rather than sprinkling assertions at each site is
 * deliberate: a new `db_*` call added to the grid later inherits the check
 * instead of quietly bypassing it.
 *
 * Scope, honestly stated: this is a JS-side control over JS-side callers. It
 * makes the Settings toggle mean what it says for the surfaces the user is
 * actually looking at. It is NOT a boundary against host-realm code that has
 * already been compromised — such code can call `invoke` itself. The strong
 * version of this is a per-extension grant table pushed down into Rust at
 * grant/revoke time, which is a larger change and is recorded as a follow-up.
 *
 * WHO IT APPLIES TO (05.08.2026): third-party marketplace extensions, and only
 * them. Compiled-in first-party extensions are exempt — the toggle this gate
 * exists to make truthful does not exist for them, and they can reach `invoke`
 * without asking. See `isCapabilityGateExempt` below for the full argument and,
 * more importantly, for why the exemption keys off the compiled-in id list
 * rather than a manifest field an installable bundle could set on itself.
 */

/**
 * Tauri command -> the capability the user must have granted to reach it.
 *
 * An array means "any one of these is enough". Only db_stop_query needs that:
 * it is engine-agnostic by design (one command for all three rather than three
 * near-identical ones), so the honest requirement is that the caller holds SOME
 * database grant — whichever engine it is about to stop.
 */
export const CAPABILITY_BY_COMMAND = {
  db_stop_query: ['db:mysql', 'db:postgres', 'db:sqlite'],
  // Card 6.4: a vault-stored DB Manager connection; Rust also refuses any other extension.
  db_connect_vault: ['db:mysql', 'db:postgres'],
  db_mysql_connect: 'db:mysql',
  db_mysql_ping: 'db:mysql',
  db_mysql_query: 'db:mysql',
  db_mysql_query_multi: 'db:mysql',
  db_mysql_execute_batch: 'db:mysql',
  db_postgres_connect: 'db:postgres',
  db_postgres_ping: 'db:postgres',
  db_postgres_query: 'db:postgres',
  db_postgres_query_multi: 'db:postgres',
  db_postgres_execute_batch: 'db:postgres',
  db_sqlite_connect: 'db:sqlite',
  db_sqlite_ping: 'db:sqlite',
  db_sqlite_query: 'db:sqlite',
  db_sqlite_query_multi: 'db:sqlite',
  db_sqlite_execute_batch: 'db:sqlite',
  db_scripts_list: 'db:scripts',
  db_script_read: 'db:scripts',
  db_script_write: 'db:scripts',
  db_script_delete: 'db:scripts',
  secure_storage_get: 'secureStorage',
  secure_storage_set: 'secureStorage',
  secure_storage_delete: 'secureStorage',
};

export function requiredCapabilityFor(command) {
  return CAPABILITY_BY_COMMAND[command] || null;
}

/**
 * Compiled-in first-party extensions are NOT subject to the capability gate.
 *
 * WHY THE GATE EXISTS AT ALL: to make the per-capability toggle in Settings >
 * Extensions truthful. A user who turns `db:mysql` off must stop being able to
 * run MySQL through that extension — otherwise the switch is a label that lies.
 * That is the entire justification, and it is a good one for third-party
 * marketplace code, whose grants exist ONLY because a consent flow asked for
 * them.
 *
 * WHY IT IS THEATRE HERE: the extensions in `bundled/index.js` are first-party
 * source compiled into this binary. bundled/index.js hands them their whole
 * declared capability set at boot with no consent screen, Settings offers them
 * no per-capability toggle (there is no consent to withdraw, so there is
 * nothing for a toggle to mean), and every one of them is host-realm React that
 * can `import { invoke } from '@tauri-apps/api/core'` and call the same Tauri
 * command directly. A check in front of code that can step around the check is
 * not a boundary. The project's position, stated in
 * extensionCapabilityEnforcement.test.js and again in SettingsModal.js, is that
 * honest labelling beats fake enforcement — so these are labelled `hostRendered`
 * and declare `permissions: []`, and the gate says so out loud instead of
 * pretending to hold them back.
 *
 * ── WHAT THIS KEYS OFF, AND WHY IT IS NOT NEGOTIABLE ────────────────────────
 * `isShippedBundledExtension` — an id set built from the STATIC IMPORTS in
 * bundled/index.js — and never `manifest.hostRendered`.
 *
 * bootstrap.js registers marketplace-installed extensions from CACHED BUNDLES
 * on disk, and each of those carries its own manifest (see the `installedFrom
 * Marketplace` loop). If this exemption trusted a manifest field, any
 * third-party extension could ship `hostRendered: true` in its own manifest and
 * turn off every capability check in the product for itself — a privilege
 * escalation reachable by editing a JSON file. Nothing can add an id to the
 * compiled-in set: it is fixed when the binary is built, and bootstrap.js
 * additionally refuses to let a cached bundle shadow a compiled-in id.
 * `extensionCapabilityExemption.test.js` fails if this is ever re-keyed.
 *
 * Scope of the exemption, stated plainly: it is not "this extension may do
 * more", it is "this extension was never held back in the first place, and the
 * code now admits it."
 */
export function isCapabilityGateExempt(extensionId) {
  return !!extensionId && isShippedBundledExtension(extensionId);
}

/**
 * The capabilities `extensionId` effectively holds right now — the persisted
 * grant set, except for compiled-in first-party extensions, which hold
 * everything this build knows about (see `isCapabilityGateExempt`).
 *
 * One definition, three consumers, so no reader has to wonder whether a given
 * layer implements the exemption: this module's own gate, the DB connection
 * picker's "which extensions can reach a database" question
 * (capabilities/db.js), and the grant set handed to a guest iframe's bridge
 * (ExtensionHostLayer.js). The last one matters most: db-manager's schema tree
 * runs in the sandboxed guest, and sdk/hostBridge.js gates RPC on the grant set
 * it is GIVEN. Leaving that one out would not "keep the guest honest", it would
 * simply delete the tree, the connection dialogs and the per-engine
 * disconnect-on-teardown backstop in ExtensionHost.js.
 *
 * hostBridge.js itself is deliberately untouched: it stays a plain, unconditional
 * "is this capability in the set I was handed?" check, which is exactly what a
 * third-party guest still meets.
 */
export function effectiveGrantedCapabilities(extensionId) {
  if (!extensionId) return [];
  if (isCapabilityGateExempt(extensionId)) return [...KNOWN_CAPABILITIES];
  return useExtensionStore.getState().grantedCapabilities[extensionId] || [];
}

export function hasCapability(extensionId, capability) {
  if (!extensionId) return false;
  return effectiveGrantedCapabilities(extensionId).includes(capability);
}

/**
 * Throws unless `extensionId` currently holds `capability`. Read live from the
 * store on every call — a revoke in Settings must block the NEXT call, not
 * take effect on the next remount (same rule hostBridge follows).
 */
export function assertCapability(extensionId, capability) {
  // An array requirement is satisfied by holding any one of its entries.
  if (Array.isArray(capability)) {
    if (capability.some((c) => hasCapability(extensionId, c))) return;
    throw new Error(
      `Extension "${extensionId}" holds none of the capabilities ${capability.join(', ')}`
    );
  }
  if (!hasCapability(extensionId, capability)) {
    throw new Error(
      `Extension "${extensionId}" is missing granted capability "${capability}"`
    );
  }
}

/**
 * `invoke`, gated on the grant the command needs. Commands with no entry in
 * CAPABILITY_BY_COMMAND pass straight through.
 *
 * The db_script_* commands used to be in that second category, on the reasoning
 * that they touch the user's own files rather than a database. That was the
 * wrong line to draw: `default.json` deliberately denies the fs plugin
 * `$APPLOCALDATA/users/*​/scripts/**` so webview code cannot read saved SQL, and
 * these four commands handed back read, enumerate, write and delete over
 * exactly that tree with no check — a way around a deny that was put there on
 * purpose. They now need `db:scripts`.
 */
export function invokeAsExtension(command, args) {
  const capability = requiredCapabilityFor(command);
  if (capability && (!Array.isArray(capability) || capability.length > 0)) {
    try {
      assertCapability(args?.extensionId, capability);
    } catch (err) {
      return Promise.reject(err);
    }
  }
  return invoke(command, args);
}
